import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Beaker, Shield, Award, Target, Users, Heart,
  CheckCircle, Microscope, Leaf, Globe, Zap, Star
} from 'lucide-react';

const AboutUsPage = () => {
  const values = [
    {
      icon: Shield,
      title: 'Quality First',
      description: 'Every peptide we sell undergoes rigorous testing to ensure 99%+ purity. We never compromise on quality.'
    },
    {
      icon: Microscope,
      title: 'Science-Driven',
      description: 'Our team includes experts in peptide chemistry who understand the demands of modern research.'
    },
    {
      icon: Users,
      title: 'Customer Focus',
      description: 'We treat every researcher as a partner, providing exceptional support and fast response times.'
    },
    {
      icon: Zap,
      title: 'Speed & Reliability',
      description: 'Same-day shipping on most orders means your research never has to wait.'
    }
  ];

  const milestones = [
    { year: '2019', event: 'AminoChain founded with a mission to democratize access to research peptides' },
    { year: '2020', event: 'Expanded product catalog to 50+ peptides, launched wholesale program' },
    { year: '2021', event: 'Achieved ISO-certified quality management, served 1,000+ researchers' },
    { year: '2022', event: 'Introduced same-day shipping, expanded to all 50 states' },
    { year: '2023', event: 'Launched advanced research library, reached 5,000+ customers' },
    { year: '2024', event: 'Expanded facilities, introduced new peptide synthesis capabilities' },
    { year: '2025', event: 'Continuing to innovate and support groundbreaking research worldwide' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 text-white py-20 pt-36">
        <div className="max-w-6xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center text-purple-200 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Link>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                Advancing Research,<br />
                <span className="text-amber-400">One Peptide at a Time</span>
              </h1>
              <p className="text-xl text-purple-200 leading-relaxed">
                AminoChain is dedicated to providing researchers worldwide with the highest quality 
                peptides, exceptional service, and the resources they need to push the boundaries of science.
              </p>
            </div>
            <div className="hidden lg:flex justify-center">
              <div className="relative">
                <div className="w-64 h-64 rounded-full bg-gradient-to-br from-purple-600/30 to-amber-500/20 flex items-center justify-center">
                  <Beaker className="w-32 h-32 text-amber-400" />
                </div>
                <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Star className="w-10 h-10 text-amber-400" />
                </div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full bg-purple-500/30 flex items-center justify-center">
                  <Shield className="w-8 h-8 text-purple-300" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Our Story */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                AminoChain was founded in 2019 by a team of researchers who experienced firsthand 
                the challenges of sourcing high-quality peptides for their work. Too often, they 
                encountered inconsistent purity levels, unreliable suppliers, and poor customer service.
              </p>
              <p>
                We set out to build something different—a company that researchers could trust completely. 
                From day one, our commitment has been unwavering: provide the highest quality research 
                peptides, backed by transparent documentation and exceptional customer support.
              </p>
              <p>
                Today, AminoChain serves thousands of researchers across the United States, from 
                university laboratories to biotech startups. Our 99%+ purity guarantee, comprehensive 
                Certificates of Analysis, and same-day shipping have made us a trusted partner in 
                advancing scientific discovery.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Award className="w-6 h-6 text-amber-500" />
              By the Numbers
            </h3>
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center p-4 bg-purple-50 rounded-xl">
                <p className="text-3xl font-bold text-purple-600">99%+</p>
                <p className="text-sm text-gray-600">Purity Guaranteed</p>
              </div>
              <div className="text-center p-4 bg-amber-50 rounded-xl">
                <p className="text-3xl font-bold text-amber-600">5,000+</p>
                <p className="text-sm text-gray-600">Happy Researchers</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-xl">
                <p className="text-3xl font-bold text-green-600">100+</p>
                <p className="text-sm text-gray-600">Products Available</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-xl">
                <p className="text-3xl font-bold text-blue-600">Same Day</p>
                <p className="text-sm text-gray-600">Shipping Available</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mission */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Target className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
          <p className="text-xl text-purple-100 leading-relaxed max-w-3xl mx-auto">
            To accelerate scientific discovery by providing researchers with premium-quality peptides, 
            unmatched service, and educational resources—making advanced research accessible to 
            laboratories of all sizes.
          </p>
        </div>
      </div>

      {/* Our Values */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Values</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => {
            const Icon = value.icon;
            return (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-100 to-amber-100 flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-7 h-7 text-purple-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-gray-100 py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Journey</h2>
          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-purple-200 transform md:-translate-x-1/2" />
            
            {milestones.map((milestone, index) => (
              <div key={index} className={`relative flex items-center mb-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                {/* Dot */}
                <div className="absolute left-4 md:left-1/2 w-4 h-4 bg-purple-600 rounded-full transform -translate-x-1/2 z-10" />
                
                {/* Content */}
                <div className={`ml-12 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                  <div className="bg-white rounded-lg shadow-md p-4 inline-block">
                    <span className="text-purple-600 font-bold text-lg">{milestone.year}</span>
                    <p className="text-gray-600 mt-1">{milestone.event}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quality Promise */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="bg-gradient-to-br from-purple-900 to-purple-800 rounded-2xl p-8 lg:p-12 text-white">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Our Quality Promise</h2>
              <ul className="space-y-4">
                {[
                  'Every batch tested to 99%+ purity via HPLC',
                  'Full Certificate of Analysis with every product',
                  'Temperature-controlled storage and shipping',
                  'Rigorous quality control at every step',
                  'Transparent sourcing and manufacturing'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                    <span className="text-purple-100">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-center">
              <div className="inline-block p-8 bg-white/10 rounded-2xl backdrop-blur">
                <Shield className="w-20 h-20 text-amber-400 mx-auto mb-4" />
                <p className="text-2xl font-bold">99%+ Purity</p>
                <p className="text-purple-200">Guaranteed on Every Product</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-gray-100 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Partner with Us?</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Join thousands of researchers who trust AminoChain for their peptide needs. 
            Experience the difference that quality, reliability, and exceptional service can make.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/premium-peptides"
              className="px-8 py-4 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
            >
              Browse Products
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 border-2 border-purple-600 text-purple-600 font-semibold rounded-lg hover:bg-purple-50 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>

      {/* Contact Info */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Get in Touch</h3>
          <p className="text-gray-600 mb-4">
            Have questions? We'd love to hear from you.
          </p>
          <div className="inline-block p-4 bg-purple-50 rounded-lg">
            <p className="font-semibold text-purple-900">AminoChain</p>
            <p className="text-purple-700">support@amino-chain.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUsPage;
